import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useState, useEffect } from 'react'
import messages from './messages'

const CommonPropTypes = {
  type: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  values: PropTypes.array,
  error: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  label: PropTypes.string,
  subLabels: PropTypes.array.isRequired,
  suffixes: PropTypes.array.isRequired,
  icon: PropTypes.string.isRequired,
  value: PropTypes.number,
  onBlur: PropTypes.func,
  suffix: PropTypes.any.isRequired,
  labelSuffix: PropTypes.bool,
  onFocus: PropTypes.func,
  dual: PropTypes.bool,
  subLabel: PropTypes.string,
  showSuffix: PropTypes.bool,
  suffixOffset: PropTypes.number,
  passedRef: PropTypes.object,
  showIcon: PropTypes.bool,
  dualType: PropTypes.oneOf([0, 1]),
}

const StatsField = ({
  type,
  dual = false,
  dualType,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  onKeyDown,
  passedRef,
  subLabel,
  suffix,
  label,
  labelSuffix,
}) => {
  return (
    <div className="mb-11 text-left">
      <label
        className="text-14 font-demibold"
        htmlFor={`${type}Input${dual ? dualType : ''}`}
      >
        {label && <FormattedMessage {...messages[label]} />}
        {labelSuffix && ` (${suffix})`}
        {subLabel}
      </label>
      <input
        data-jump-target
        data-uid={`signup_flow_${type}_input${dual ? `_${dualType}` : ''}`}
        className={
          'rounded-8 bg-BgMain px-20 pb-16 pt-[18px] text-TypeSub transition type-14-demibold'
        }
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        type="text"
        placeholder={placeholder}
        inputMode="numeric"
        pattern="[0-9]*"
        id={`${type}Input${dual ? dualType : ''}`}
        value={value !== null ? value : ''}
        ref={passedRef}
      />
    </div>
  )
}

StatsField.propTypes = {
  type: CommonPropTypes.type,
  value: CommonPropTypes.value,
  onChange: CommonPropTypes.onChange,
  onBlur: CommonPropTypes.onBlur,
  onKeyDown: CommonPropTypes.onKeyDown,
  label: CommonPropTypes.label,
  suffix: CommonPropTypes.suffix,
  placeholder: CommonPropTypes.placeholder,
  labelSuffix: CommonPropTypes.labelSuffix,
  icon: CommonPropTypes.icon,
  onFocus: CommonPropTypes.onFocus,
  dual: CommonPropTypes.dual,
  subLabel: CommonPropTypes.subLabel,
  showSuffix: CommonPropTypes.showSuffix,
  suffixOffset: CommonPropTypes.suffixOffset,
  passedRef: CommonPropTypes.passedRef,
  showIcon: CommonPropTypes.showIcon,
  dualType: CommonPropTypes.dualType,
}

export function StatsInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  error,
  suffix,
  ...other
}) {
  const suffixCriteria = !error && value !== null
  const [showSuffix, setShowSuffix] = useState(suffixCriteria)
  const [suffixOffset, setSuffixOffset] = useState(0)

  const showOrHideSuffix = () => {
    setShowSuffix(suffixCriteria)

    if (suffixCriteria) {
      let helperElement = document.createElement('div')
      helperElement.innerHTML = value + '&nbsp;'
      helperElement.style.position = 'absolute'
      helperElement.style.left = '-9999px'
      document.getElementsByTagName('body')[0].appendChild(helperElement)
      const width = helperElement.clientWidth
      setSuffixOffset(width)
      helperElement && helperElement.parentNode.removeChild(helperElement)
    }
  }

  useEffect(() => {
    showOrHideSuffix()
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, suffix])

  const handleBlur = () => {
    showOrHideSuffix()
    if (onBlur) onBlur()
  }

  return (
    <div className="relative mb-10" data-jump-wrapper>
      <div className="text-left">
        <StatsField
          onChange={(e) =>
            onChange(
              e.target.value.length && !isNaN(parseInt(e.target.value, 10))
                ? parseInt(e.target.value, 10)
                : null,
            )
          }
          onFocus={() => setShowSuffix(false)}
          onBlur={handleBlur}
          onKeyDown={(e) => onKeyDown(e.key)}
          showSuffix={showSuffix}
          suffixOffset={suffixOffset}
          value={value}
          suffix={suffix}
          {...other}
        />
      </div>
      {error && (
        <div
          className="c-signup-flow__error-bubble z-20"
          data-uid="error_message"
        >
          <div className="c-error-bubble">{error.errorMessage}</div>
        </div>
      )}
    </div>
  )
}

StatsInput.propTypes = {
  type: CommonPropTypes.type,
  value: CommonPropTypes.value,
  error: CommonPropTypes.error,
  onChange: CommonPropTypes.onChange,
  onBlur: CommonPropTypes.onBlur,
  onKeyDown: CommonPropTypes.onKeyDown,
  label: CommonPropTypes.label,
  suffix: CommonPropTypes.suffix,
  labelSuffix: CommonPropTypes.labelSuffix,
  icon: CommonPropTypes.icon,
}

export function StatsInputDual({
  type,
  values,
  onChange,
  onKeyDown,
  error,
  label,
  subLabels,
  suffixes,
  icon,
}) {
  const inputRef1 = useRef()
  const inputRef2 = useRef()

  const iconCriteria =
    !error && values !== null && values[0] !== null && values[1] !== null
  const [showIcon, setShowIcon] = useState()

  const suffix1Criteria = values !== null && values[0] !== null
  const [showSuffix1, setShowSuffix1] = useState()
  const [suffix1Offset, setSuffix1Offset] = useState(0)

  const suffix2Criteria = values !== null && values[1] !== null
  const [showSuffix2, setShowSuffix2] = useState()
  const [suffix2Offset, setSuffix2Offset] = useState(0)

  useEffect(() => {
    setShowIcon(iconCriteria)
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, error])

  const setSuffixOffset = (which) => {
    let helperElement = document.createElement('div')
    helperElement.innerHTML =
      values[which] + (type === 'height' ? '' : '&nbsp;')
    helperElement.style.position = 'absolute'
    helperElement.style.left = '-9999px'
    document.getElementsByTagName('body')[0].appendChild(helperElement)
    const width = helperElement.clientWidth
    which === 0 ? setSuffix1Offset(width) : setSuffix2Offset(width)
    helperElement && helperElement.parentNode.removeChild(helperElement)
  }

  const showOrHideSuffix1 = () => {
    setShowSuffix1(suffix1Criteria)
    if (suffix1Criteria) setSuffixOffset(0)
  }

  const showOrHideSuffix2 = () => {
    setShowSuffix2(suffix2Criteria)
    if (suffix2Criteria) setSuffixOffset(1)
  }

  useEffect(() => {
    showOrHideSuffix1()
    showOrHideSuffix2()
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="relative text-left" data-jump-wrapper>
      <span className="mb-10 text-14">
        <FormattedMessage {...messages[label]} />
      </span>
      <div className="flex">
        <div className="mr-4" data-jump-wrapper>
          <StatsField
            onChange={(e) =>
              onChange([
                e.target.value.length && !isNaN(parseInt(e.target.value, 10))
                  ? parseInt(e.target.value, 10)
                  : null,
                inputRef2.current.value.length
                  ? parseInt(inputRef2.current.value, 10)
                  : null,
              ])
            }
            onFocus={() => {
              setShowIcon(false)
              setShowSuffix1(false)
            }}
            onBlur={() => {
              setShowIcon(iconCriteria)
              showOrHideSuffix1()
            }}
            onKeyDown={(e) => onKeyDown(e.key)}
            showSuffix={showSuffix1}
            suffixOffset={suffix1Offset}
            subLabel={subLabels[0]}
            value={values && values[0]}
            suffix={suffixes[0]}
            passedRef={inputRef1}
            type={type}
            icon={icon}
            dual
            dualType={0}
          />
        </div>
        <div className="ml-4" data-jump-wrapper data-jump-wrapper-dual>
          <StatsField
            onChange={(e) =>
              onChange([
                inputRef1.current.value.length
                  ? parseInt(inputRef1.current.value, 10)
                  : null,
                e.target.value.length && !isNaN(parseInt(e.target.value, 10))
                  ? parseInt(e.target.value, 10)
                  : null,
              ])
            }
            onFocus={() => {
              setShowIcon(false)
              setShowSuffix2(false)
            }}
            onBlur={() => {
              setShowIcon(iconCriteria)
              showOrHideSuffix2()
            }}
            onKeyDown={(e) => onKeyDown(e.key)}
            showSuffix={showSuffix2}
            suffixOffset={suffix2Offset}
            subLabel={subLabels[1]}
            value={values && values[1]}
            suffix={suffixes[1]}
            passedRef={inputRef2}
            type={type}
            icon={icon}
            dual
            dualType={1}
            showIcon={showIcon}
          />
        </div>
      </div>
      {error && (
        <div
          className="c-signup-flow__error-bubble z-20"
          data-uid="error_message"
        >
          <div className="c-error-bubble text-14">{error.errorMessage}</div>
        </div>
      )}
    </div>
  )
}

StatsInputDual.propTypes = {
  type: CommonPropTypes.type,
  values: CommonPropTypes.values,
  error: CommonPropTypes.error,
  onChange: CommonPropTypes.onChange,
  onKeyDown: CommonPropTypes.onKeyDown,
  label: CommonPropTypes.label,
  subLabels: CommonPropTypes.subLabels,
  suffixes: CommonPropTypes.suffixes,
  icon: CommonPropTypes.icon,
}

export default [StatsInput, StatsInputDual]
